import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Briefcase, User } from "lucide-react";
import { onboardingSchema } from "@/lib/validation";
import { z } from "zod";

interface OnboardingFlowProps {
  onComplete: () => void;
}

const businessTypes = [
  "Uber / Lyft / Rideshare",
  "Instacart / DoorDash / Delivery",
  "Shopify / E-commerce",
  "Freelancer / Consultant",
  "Real Estate",
  "Content Creator",
  "Construction / Trades",
  "Restaurant / Food Service",
  "Other"
];

export const OnboardingFlow = ({ onComplete }: OnboardingFlowProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [useCase, setUseCase] = useState<"personal" | "business">("personal");
  const [businessType, setBusinessType] = useState("");
  const [customBusinessType, setCustomBusinessType] = useState("");
  const [income, setIncome] = useState("");

  const getSummaryMessage = () => {
    const finalBusinessType = businessType === "Other" ? customBusinessType : businessType;
    const useCaseText = useCase === "personal" ? "personal use" : finalBusinessType;
    const incomeText = income ? `, around $${income}` : "";
    
    return `Got it — ${useCaseText}${incomeText}. Let's start finding ways to save you money.`;
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const finalBusinessType = businessType === "Other" ? customBusinessType : businessType;

      // Validate input before database update
      const validated = onboardingSchema.parse({
        use_case: useCase,
        business_type: useCase === "business" ? finalBusinessType : "",
        estimated_income: income
      });

      const { error } = await supabase
        .from("profiles")
        .update({
          use_case: validated.use_case,
          business_type: useCase === "business" ? validated.business_type : null,
          onboarding_completed: true
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: getSummaryMessage(),
        description: "Your tax assistant is ready to help you save!",
      });

      onComplete();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error saving onboarding:", error);
        }
        toast({
          title: "Oops!",
          description: "Couldn't save your preferences. Give it another shot!",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center space-y-3 animate-fade-in">
          <h1 className="text-3xl md:text-4xl font-semibold">
            {step === 1 && "Welcome! Let's get started"}
            {step === 2 && "Tell us about your work"}
            {step === 3 && "One more thing"}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            {step === 1 && "Is this for personal or business use?"}
            {step === 2 && "What kind of work or business do you do?"}
            {step === 3 && "Roughly how much did you earn last year?"}
          </p>
        </div>

        <div className="space-y-4">
          {step === 1 && (
            <>
              <div className="grid gap-3 animate-fade-in">
                <Button
                  variant={useCase === "personal" ? "default" : "outline"}
                  className="h-auto p-6 justify-start text-left group transition-all duration-300 hover:scale-[1.02] animate-slide-up"
                  style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}
                  onClick={() => setUseCase("personal")}
                >
                  <User className="w-6 h-6 mr-4 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-base">Personal</div>
                    <div className="text-sm text-muted-foreground group-hover:text-foreground">
                      Just tracking my own expenses
                    </div>
                  </div>
                </Button>
                <Button
                  variant={useCase === "business" ? "default" : "outline"}
                  className="h-auto p-6 justify-start text-left group transition-all duration-300 hover:scale-[1.02] animate-slide-up"
                  style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}
                  onClick={() => setUseCase("business")}
                >
                  <Briefcase className="w-6 h-6 mr-4 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-base">Business</div>
                    <div className="text-sm text-muted-foreground group-hover:text-foreground">
                      Running a business and maximizing deductions
                    </div>
                  </div>
                </Button>
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={() => setStep(useCase === "business" ? 2 : 3)} size="lg" className="transition-all duration-300 hover:scale-105">
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <RadioGroup value={businessType} onValueChange={setBusinessType} className="space-y-2 animate-fade-in">
                {businessTypes.map((type, index) => (
                  <label
                    key={type}
                    className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-all duration-300 hover:scale-[1.01] animate-slide-up"
                    style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'backwards' }}
                  >
                    <RadioGroupItem value={type} id={type} />
                    <Label htmlFor={type} className="cursor-pointer ml-3 text-base flex-1">
                      {type}
                    </Label>
                  </label>
                ))}
              </RadioGroup>
              {businessType === "Other" && (
                <div className="space-y-1 animate-fade-in">
                  <Input
                    placeholder="e.g., Photography studio, Dog grooming"
                    value={customBusinessType}
                    onChange={(e) => setCustomBusinessType(e.target.value)}
                    maxLength={100}
                    className="mt-3 transition-all duration-300 focus:scale-[1.01]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Max 100 characters
                  </p>
                </div>
              )}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)} size="lg" className="transition-all duration-300 hover:scale-105">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  disabled={!businessType || (businessType === "Other" && !customBusinessType.trim())}
                  size="lg"
                  className="transition-all duration-300 hover:scale-105"
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-3 animate-fade-in">
                <Input
                  type="text"
                  placeholder="e.g., 45000"
                  value={income}
                  onChange={(e) => setIncome(e.target.value.replace(/\D/g, ''))}
                  maxLength={9}
                  className="text-base h-12 transition-all duration-300 focus:scale-[1.01]"
                />
                <p className="text-sm text-muted-foreground">
                  Just a rough estimate — helps us give you better advice (optional, max 100,000,000)
                </p>
              </div>
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(useCase === "business" ? 2 : 1)} size="lg" className="transition-all duration-300 hover:scale-105">
                  Back
                </Button>
                <Button onClick={handleComplete} disabled={loading} size="lg" className="transition-all duration-300 hover:scale-105">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? "Setting up..." : "Get Started"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};